// Admin GST & Billing Settings
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Download, ReceiptText } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const DEFAULTS = {
  business_legal_name: 'Thachari Interior Training Pvt Ltd',
  gstin: '',
  business_address: '',
  state: 'Tamil Nadu',
  pan_number: '',
  gst_rate: 18,
  force_igst: false,
  invoice_prefix: 'THAC',
  invoice_starting_number: 1,
  show_gst_on_invoice: true,
  send_invoice_email: true,
  invoice_footer_note: 'This is a computer generated invoice.',
};

export const AdminGSTSettings = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [states, setStates] = useState([]);
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, statesRes] = await Promise.all([
          axios.get(`${API_URL}/settings/gst`),
          axios.get(`${API_URL}/gst/states`),
        ]);
        setForm({ ...DEFAULTS, ...settingsRes.data });
        setStates(statesRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load GST settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const validateGstin = (value) => {
    if (!value) return true;
    return GSTIN_REGEX.test(value.toUpperCase());
  };
  const validatePan = (value) => {
    if (!value) return true;
    return PAN_REGEX.test(value.toUpperCase());
  };

  const onGstinBlur = () => {
    setErrors((e) => ({ ...e, gstin: validateGstin(form.gstin) ? null : 'Invalid GSTIN format. Expected e.g. 22AAAAA0000A1Z5' }));
  };
  const onPanBlur = () => {
    setErrors((e) => ({ ...e, pan_number: validatePan(form.pan_number) ? null : 'Invalid PAN format. Expected e.g. AAAAA0000A' }));
  };

  const handleSave = async () => {
    if (!validateGstin(form.gstin)) {
      setErrors((e) => ({ ...e, gstin: 'Invalid GSTIN format. Expected e.g. 22AAAAA0000A1Z5' }));
      toast.error('Please fix the GSTIN before saving');
      return;
    }
    if (!validatePan(form.pan_number)) {
      setErrors((e) => ({ ...e, pan_number: 'Invalid PAN format. Expected e.g. AAAAA0000A' }));
      toast.error('Please fix the PAN before saving');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, gstin: form.gstin?.toUpperCase() || '', pan_number: form.pan_number?.toUpperCase() || '' };
      await axios.put(`${API_URL}/admin/settings/gst`, payload, { headers });
      toast.success('GST settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save GST settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API_URL}/admin/settings/gst/preview`, {}, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sample-invoice.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to generate preview invoice');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="admin-gst-settings">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">GST & Billing</h1>
        <p className="text-muted-foreground">Configure your business tax details and invoice numbering</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="w-5 h-5" /> Business & Tax Details
          </CardTitle>
          <CardDescription>Used on every generated tax invoice</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Business Legal Name *</Label>
            <Input value={form.business_legal_name} onChange={(e) => update('business_legal_name', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>GSTIN</Label>
              <Input
                value={form.gstin}
                onChange={(e) => update('gstin', e.target.value.toUpperCase())}
                onBlur={onGstinBlur}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
              {errors.gstin ? (
                <p className="text-xs text-red-500 mt-1">{errors.gstin}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Format hint: 22AAAAA0000A1Z5</p>
              )}
            </div>
            <div>
              <Label>PAN Number</Label>
              <Input
                value={form.pan_number}
                onChange={(e) => update('pan_number', e.target.value.toUpperCase())}
                onBlur={onPanBlur}
                placeholder="AAAAA0000A"
                maxLength={10}
              />
              {errors.pan_number && <p className="text-xs text-red-500 mt-1">{errors.pan_number}</p>}
            </div>
          </div>

          <div>
            <Label>Business Address</Label>
            <Textarea
              value={form.business_address}
              onChange={(e) => update('business_address', e.target.value)}
              placeholder="Street, City, State, Pincode"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>State *</Label>
              <Select value={form.state} onValueChange={(v) => update('state', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>State Code</Label>
              <Input value={states.find((s) => s.name === form.state)?.code || ''} disabled />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>GST Rate</Label>
              <Select value={String(form.gst_rate)} onValueChange={(v) => update('gst_rate', Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 5, 12, 18, 28].map((r) => (
                    <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between pt-6">
              <div>
                <Label>Always charge IGST</Label>
                <p className="text-xs text-muted-foreground">Force IGST even for same-state customers</p>
              </div>
              <Switch checked={form.force_igst} onCheckedChange={(v) => update('force_igst', v)} />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            GST type is applied automatically per invoice: CGST + SGST when the customer's state matches
            your business state, IGST when it differs (unless "Always charge IGST" is on).
          </p>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Invoice Numbering & Display</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Invoice Prefix</Label>
              <Input value={form.invoice_prefix} onChange={(e) => update('invoice_prefix', e.target.value)} placeholder="THAC" />
              <p className="text-xs text-muted-foreground mt-1">e.g. {form.invoice_prefix || 'THAC'}-2026-0001</p>
            </div>
            <div>
              <Label>Invoice Starting Number</Label>
              <Input
                type="number"
                min={1}
                value={form.invoice_starting_number}
                onChange={(e) => update('invoice_starting_number', parseInt(e.target.value || '1', 10))}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Show GST on Invoice</Label>
              <p className="text-xs text-muted-foreground">Display the GST/CGST/SGST/IGST breakdown</p>
            </div>
            <Switch checked={form.show_gst_on_invoice} onCheckedChange={(v) => update('show_gst_on_invoice', v)} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Send Invoice Email</Label>
              <p className="text-xs text-muted-foreground">Email the invoice PDF to the customer on payment</p>
            </div>
            <Switch checked={form.send_invoice_email} onCheckedChange={(v) => update('send_invoice_email', v)} />
          </div>

          <div>
            <Label>Invoice Footer Note</Label>
            <Textarea
              value={form.invoice_footer_note}
              onChange={(e) => update('invoice_footer_note', e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex items-center gap-4 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={previewing}>
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Preview Sample Invoice
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGSTSettings;
